import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface DirectoryUser {
  id: string;
  displayName: string;
  displayNameAr: string | null;
}
export interface PersonRow {
  id: string;
  displayName: string;
  nameArabic: string | null;
  jobTitle: string | null;
  lifecycleStage: 'lead' | 'contact' | 'customer' | 'churned';
  city: string | null;
  ownerName: string | null;
  accountName: string | null;
  identityTypes: string[] | null;
  lastMessageAt: string | null;
  updatedAt: string;
  /** Current marketing consent per channel (null = never recorded). */
  marketingConsent?: Record<'whatsapp' | 'email' | 'sms', 'granted' | 'withdrawn' | null>;
}
export interface AccountRow {
  id: string;
  name: string;
  nameAr: string | null;
  industry: string | null;
  city: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  vatNumber: string | null;
  ownerName: string | null;
  peopleCount?: number;
  openDeals?: number;
  openCases?: number;
}
export interface Stage {
  id: string;
  key: string;
  nameEn: string;
  nameAr: string;
  position: number;
  probability: number;
  kind: 'open' | 'won' | 'lost';
}
export interface Pipeline {
  id: string;
  key: string;
  nameEn: string;
  nameAr: string;
  stages: Stage[];
}
export interface Deal {
  id: string;
  title: string;
  amountMinor: string;
  currency: string;
  status: 'open' | 'won' | 'lost';
  stageId: string;
  accountId: string | null;
  accountName: string | null;
  personId: string | null;
  personName: string | null;
  ownerName: string | null;
  expectedCloseDate: string | null;
}
export interface CaseRow {
  id: string;
  number: string;
  subject: string;
  description: string | null;
  status: 'new' | 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  category: string | null;
  regulatorFlag: boolean;
  personId: string | null;
  personName: string | null;
  accountId: string | null;
  accountName: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  createdAt: string;
  resolvedAt: string | null;
  dueAt: string | null;
}
export interface TaskRow {
  id: string;
  title: string;
  dueAt: string | null;
  status: 'open' | 'done' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  relatedType: string | null;
  relatedId: string | null;
  relatedLabel: string | null;
  assigneeName: string | null;
}
export interface TimelineItem {
  kind: 'message' | 'note' | 'case' | 'deal' | 'task';
  id: string;
  at: string;
  sub: string | null;
  channel: string | null;
  text: string | null;
  refId: string | null;
  title: string | null;
  actor: string | null;
}

export const useDirectory = () =>
  useQuery({
    queryKey: ['directory'],
    queryFn: () => api<{ items: DirectoryUser[] }>('/v1/directory/users'),
    staleTime: 60_000,
  });
export const usePipelines = () =>
  useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api<{ items: Pipeline[] }>('/v1/pipelines'),
    staleTime: 60_000,
  });
export const usePeopleOptions = () =>
  useQuery({
    queryKey: ['people-options'],
    queryFn: () => api<{ items: PersonRow[] }>('/v1/people?limit=200'),
    staleTime: 15_000,
  });
export const useAccountOptions = () =>
  useQuery({
    queryKey: ['account-options'],
    queryFn: () => api<{ items: AccountRow[] }>('/v1/accounts?limit=200'),
    staleTime: 15_000,
  });

/** Query keys every CRM mutation invalidates (cheap; lists are small and paginated server-side). */
export const CRM_KEYS = [
  'people',
  'person',
  'accounts',
  'account',
  'deals',
  'cases',
  'case',
  'tasks',
  'timeline',
  'notes',
  'dashboard',
  'people-options',
  'account-options',
];
